import { api } from "@app/api";
import { selectEnv } from "@app/config";
import {
  findEnvById,
  hasDeployEnvironment,
  selectEnvironmentsByOrg,
} from "@app/deploy/environment";
import { call, createSelector, select } from "@app/fx";
import { defaultEntity, extractIdFromLink } from "@app/hal";
import { type WebState, schema } from "@app/schema";
import type {
  DeployLlmKey,
  DeployLlmKeyRequest,
  DeployLlmKeyRequestResponse,
  DeployLlmKeyResponse,
} from "@app/types";

export interface DeployLlmKeyRow extends DeployLlmKey {
  envHandle: string;
  totalRequests: number;
  spend: number;
  totalTokens: number;
}

// Selectors
export const selectLlmKeyById = schema.llmKeys.selectById;
export const selectLlmKeys = schema.llmKeys.selectTable;
export const selectLlmKeysAsList = schema.llmKeys.selectTableAsList;
export const findLlmKeyById = schema.llmKeys.findById;

export const selectLlmKeysByEnvId = createSelector(
  selectLlmKeysAsList,
  (_: WebState, props: { envId: string }) => props.envId,
  (tokens, envId) => {
    if (envId === "") {
      return tokens;
    }
    return tokens.filter((token) => token.environmentId === envId);
  },
);

export const selectLlmKeysByOrgAsList = createSelector(
  selectLlmKeysAsList,
  selectEnvironmentsByOrg,
  (tokens, envs) => {
    return tokens.filter((token) => {
      const env = findEnvById(envs, { id: token.environmentId });
      return hasDeployEnvironment(env);
    });
  },
);

// API endpoints
export const fetchLlmKeys = api.get<{ orgId: string }>(
  "/llm_keys?per_page=5000&organization_id=:orgId&usage=true",
  function* (ctx, next) {
    const { orgId } = ctx.payload;
    if (!orgId) {
      return;
    }

    yield* next();
    if (!ctx.json.ok) {
      return;
    }
  },
);

export const fetchLlmKeysByEnvId = api.get<{ envId: string; orgId: string }>(
  "/accounts/:envId/llm_keys?organization_id=:orgId&usage=true",
  function* (ctx, next) {
    const { envId, orgId } = ctx.payload;
    if (!envId || !orgId) {
      return;
    }

    yield* next();
    if (!ctx.json.ok) {
      return;
    }
  },
);

export const fetchLlmKeyById = api.get<{ id: string }>(
  "/llm_keys/:id?usage=true",
);

export const createLlmKey = api.post<{
  envId: string;
  handle: string;
  note: string;
  orgId: string;
}>("/accounts/:envId/llm_keys", function* (ctx, next) {
  const body = JSON.stringify({
    handle: ctx.payload.handle,
    note: ctx.payload.note,
    organization_id: ctx.payload.orgId,
  });

  ctx.request = ctx.req({ body });
  yield* next();

  if (!ctx.json.ok) {
    return;
  }

  const llmKey = ctx.json.value?.token || "";
  ctx.loader = { meta: { llmKey } };
});

export const revokeLlmKey = api.delete<{ id: string; orgId: string }>(
  "/llm_keys/:id?organization_id=:orgId",
);

export const parseModelsStr = (s: string): string[] =>
  s
    .split(/\n/)
    .map((line) => line.trim())
    .filter(Boolean);

export type LlmModelsResponse = Array<{ id: string; supported: boolean }>;

export const fetchLlmModels = api.get<never, LlmModelsResponse>(
  "/metadata/models",
  function* (ctx, next) {
    // Get LLM Gateway URL from config
    const env = yield* select(selectEnv);
    const url = `${env.llmGatewayUrl}/metadata/models`;

    // Update request URL
    ctx.request = ctx.req({ url });

    // Remove Authorization header for public endpoint
    (ctx as any).noToken = true;

    yield* next();

    if (!ctx.json.ok) {
      return;
    }

    /* Store models in loader meta for component access
     *
     * Note: we're not caching this in the starfx schema because accessible
     * models will be different per-key once model restrictions are implemented.
     */
    const models = ctx.json.value || [];
    ctx.loader = { meta: { models } };
  },
);

export type RequestsSortBy =
  | ""
  | "start_time"
  | "end_time"
  | "total_tokens"
  | "status"
  | "model"
  | "spend";

export type RequestsSortOrder = "asc" | "desc";
export type RequestsStatus = "" | "success" | "failure";

export const fetchLlmKeyRequests = api.get<{
  id: string;
  startTime?: string;
  endTime?: string;
  status?: RequestsStatus;
  model?: string;
  requestId?: string;
  sortBy?: RequestsSortBy;
  sortOrder?: "asc" | "desc";
  page?: number;
  perPage?: number;
}>("/llm_keys/:id/requests", function* (ctx, next) {
  const {
    id,
    startTime,
    endTime,
    status,
    model,
    requestId,
    sortBy,
    sortOrder,
    page = 1,
    perPage = 30,
  } = ctx.payload;

  // Build query params
  const params = new URLSearchParams();
  if (startTime) params.append("start_time", startTime);
  if (endTime) params.append("end_time", endTime);
  if (status) params.append("status", status);
  if (model) params.append("model", model);
  if (requestId) params.append("request_id", requestId);
  if (sortBy) params.append("sort_by", sortBy);
  if (sortOrder) params.append("sort_order", sortOrder);
  params.append("page", String(page));
  params.append("per_page", String(perPage));

  // Build URL with query params (start with base URL to get proper host)
  const baseUrl = ctx.req().url;
  const url = new URL(baseUrl);
  params.forEach((value, key) => {
    url.searchParams.set(key, value);
  });
  ctx.request = ctx.req({ url: url.toString() });

  yield* next();

  if (!ctx.json.ok) {
    return;
  }

  const requests = ctx.json.value?._embedded?.requests || [];
  const deserialized = requests.map((r: DeployLlmKeyRequestResponse) =>
    deserializeLlmKeyRequest(r, id),
  );

  // Reset the table to clear any previous results
  yield* schema.update(schema.llmKeyRequests.reset());

  // Add new results
  for (const r of deserialized) {
    yield* schema.update(schema.llmKeyRequests.add({ [r.id]: r }));
  }

  // Store pagination info in loader meta
  ctx.loader = {
    meta: {
      totalCount: ctx.json.value.total_count,
      perPage: ctx.json.value.per_page,
      totalPages: Math.ceil(
        ctx.json.value.total_count / ctx.json.value.per_page,
      ),
      currentPage: ctx.json.value.current_page,
    },
  };
});

export const fetchLlmKeyRequestLogs = api.get<
  { id: string; requestId: string },
  string
>("/llm_keys/:id/requests/:requestId/logs", function* (ctx, next) {
  const { id, requestId } = ctx.payload;
  if (!id || !requestId) return;

  ctx.cache = true;
  ctx.bodyType = "text";
  yield* next();

  if (!ctx.json.ok) return;

  const url = (ctx.json.value as string).trim();
  const response = yield* call(() => fetch(url));
  const body = yield* call(() => response.text());

  if (!response.ok) {
    ctx.json = { ok: false, error: { message: body } };
    return;
  }

  // Overwrite the presigned URL with the actual log content so the cache
  // stores the payload directly, matching the fetchOperationLogs pattern.
  ctx.json.value = body;
});

export const selectLlmKeyRequestById = schema.llmKeyRequests.selectById;
export const selectLlmKeyRequestsAsList =
  schema.llmKeyRequests.selectTableAsList;

export const selectLlmKeyRequestsByKeyId = createSelector(
  selectLlmKeyRequestsAsList,
  (_: WebState, props: { llmKeyId: string }) => props.llmKeyId,
  (requests, llmKeyId) => {
    return requests.filter((req) => req.llmKeyId === llmKeyId);
  },
);

export const deserializeLlmKey = (
  payload: DeployLlmKeyResponse,
): DeployLlmKey => {
  // Special handling for the usage object, which could be null in the case of
  // an upstream error between deploy-api and the LLM gateway.
  const u = payload.current_usage;
  const spend = u?.spend ?? 0;
  const successfulRequests = u?.successful_requests ?? 0;
  const failedRequests = u?.failed_requests ?? 0;
  const inputTokens = u?.input_tokens ?? 0;
  const outputTokens = u?.output_tokens ?? 0;
  const totalTokens = inputTokens + outputTokens;
  const totalRequests = successfulRequests + failedRequests;

  return {
    id: `${payload.id}`,
    handle: payload.handle,
    note: payload.note,
    gatewayUrl: payload.gateway_url,
    createdBy: payload.created_by,
    revokedAt: payload.revoked_at,
    revokedBy: payload.revoked_by,
    status: payload.status,
    environmentId: extractIdFromLink(payload._links.account),
    createdAt: payload.created_at,
    updatedAt: payload.updated_at,
    token: payload.token,
    currentUsage: {
      spend,
      inputTokens,
      outputTokens,
      successfulRequests,
      failedRequests,
      totalRequests,
      totalTokens,
    },
  };
};

const deserializeLlmKeyRequest = (
  payload: DeployLlmKeyRequestResponse,
  llmKeyId: string,
): DeployLlmKeyRequest => {
  const startMs = new Date(payload.start_time).getTime();
  const endMs = new Date(payload.end_time).getTime();
  const durationSeconds = (endMs - startMs) / 1000;
  const totalTokens = payload.input_tokens + payload.output_tokens;

  return {
    id: payload._id,
    llmKeyId,
    status: payload.status,
    startTime: payload.start_time,
    endTime: payload.end_time,
    model: payload.model,
    inputTokens: payload.input_tokens,
    outputTokens: payload.output_tokens,
    spend: payload.spend,
    durationSeconds,
    totalTokens,
    createdAt: payload.start_time,
    updatedAt: payload.end_time,
  };
};

export const llmKeyEntities = {
  llm_key: defaultEntity({
    id: "llm_key",
    deserialize: deserializeLlmKey,
    save: schema.llmKeys.add,
  }),
};
